'use client';

import './dialog.scss';

import { PropsWithChildren, ReactNode } from 'react';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { IconNames } from '@sola/schema/lib/icons/icon-names.ts';

import { ButtonStyle } from '../../atoms/button-style/button-style.tsx';
import { cn } from '../../utils/helpers.ts';
import { FormSubmit, FormSubmitProps } from '../form/form.tsx';
import {
  Modal,
  ModalClose,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalProps,
  ModalTitle,
  ModalTrigger,
  ModalTriggerProps,
} from '../modal/modal.tsx';

export type DialogProps = ModalProps;

export const Dialog = Modal;

/**
 * Dialog trigger.
 */
export type DialogTriggerProps = ModalTriggerProps;
export const DialogTrigger = ModalTrigger;

/**
 * Dialog close.
 */
export const DialogClose = ModalClose;

/**
 * Dialog content.
 */
export type DialogContentProps = ModalContentProps & {
  centered?: boolean;
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  supportingText?: ReactNode;
  purpose?: 'default' | 'neutral' | 'positive' | 'warning' | 'negative';
  customIcon?: ReactNode;
  icon?: IconNames;
};

/**
 * Primary configurable content/props required all Dialogs.
 */
export const DialogContent: React.FC<
  DialogContentProps & { ref?: React.Ref<HTMLDivElement> }
> = ({
  title,
  supportingText,
  size = 'md',
  style,
  purpose = 'neutral',
  customIcon,
  icon,
  children,
  className,
  ref,
  ...props
}) => {
  return (
    <ModalContent
      ref={ref}
      aria-describedby={undefined}
      className={cn(
        `Dialog__content Dialog__content--${size} flex flex-col overflow-y-auto gap-comfy-sm`,
        {
          [`${className}`]: !!className,
        },
      )}
      style={{
        ...style,
      }}
      {...props}
    >
      {/* Header */}
      <header className="flex flex-col">
        {/* Icon and close button */}
        <div
          className={cn('flex items-center justify-end', {
            'justify-between': icon || customIcon,
          })}
        >
          {(icon || customIcon) && (
            <div
              className={cn(
                `Dialog__icon
                Dialog__icon--${purpose} flex items-center justify-center rounded-full`,
              )}
            >
              {customIcon || (icon && <ArkIcon size="24" iconName={icon} />)}
            </div>
          )}

          <ModalCloseButton
            className="Dialog__close mr-comfy-xs mt-comfy-xs"
            name="Dialog"
          />
        </div>
      </header>
      <div className="overflow-y-auto px-snug-md -mx-snug-md">
        {/* Heading */}
        <div className="pt-comfy-sm mb-comfy-sm">
          <ModalTitle className="Dialog_title heading-sm ">{title}</ModalTitle>
          {supportingText && (
            <span className="block pt-snug-md body-sm body-neutral-mild">
              {supportingText}
            </span>
          )}
        </div>
        <div className="pt-comfy-sm md:pt-comfy-lg">{children}</div>
      </div>
    </ModalContent>
  );
};

/**
 * Optional slot for additional dialog content. Necessary for overflow-y scrolling
 */
export const DialogSlot: React.FC<PropsWithChildren> = ({ children }) => {
  return <div className="overflow-y-auto py-comfy-sm">{children}</div>;
};

/**
 * Footer containing action buttons.
 */
export const DialogActions: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <div className="Dialog__actions sticky bottom-0 left-0 pt-comfy-sm md:pt-comfy-lg flex flex-row gap-comfy-xs md:justify-end">
      {children}
    </div>
  );
};

/**
 * Dialog's primary CTA.
 */
export const DialogActionPrimary: React.FC<
  Omit<FormSubmitProps, 'className' | 'size'> & {
    type?: 'button' | 'submit';
    onClick?: () => void;
  }
> = ({ children, type = 'button', ...props }) => {
  if (type === 'submit') {
    return (
      <FormSubmit
        fullWidth={false}
        className="w-full md:w-fit"
        size="md"
        {...props}
      >
        {children}
      </FormSubmit>
    );
  }
  return (
    <ButtonStyle className="w-full md:w-fit" size="md" {...props}>
      <button type="button">{children}</button>
    </ButtonStyle>
  );
};

/**
 * Optional footer button to close/cancel the dialog.
 */
export const DialogActionClose: React.FC<
  PropsWithChildren<{ fullWidth?: boolean }>
> = ({ children, fullWidth = false }) => {
  return (
    <ButtonStyle
      className={cn('w-full', !fullWidth && 'md:w-fit')}
      size="md"
      purpose="neutral"
      variant="secondary"
      fullWidth={fullWidth}
    >
      <DialogClose>{children}</DialogClose>
    </ButtonStyle>
  );
};
